import { useStableCallback } from 'base/useStableCallback';
import React, { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import CalloutPointer from './callout-pointer.react.svg';
import styles from './callout.css';
import { Icon } from './icon';
import { IconClearSmall } from './icons';

import { Layer } from 'base/ui/Layer';
import { TextSmall, TextTinyLight } from './typography';

type CalloutProps = React.PropsWithChildren<{
  anchorRef: React.RefObject<HTMLElement>;
  visible: boolean;
  onDismiss?: () => void;
  title: string;
  delay?: number;
  body?: string;
  direction?: 'horizontal' | 'vertical';
}>;

export const Callout = ({
  anchorRef,
  visible,
  onDismiss,
  title,
  body,
  children,
  delay = 1000,
  direction = 'horizontal',
}: CalloutProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<HTMLDivElement>(null);

  const [delayedVisible, setDelayedVisible] = React.useState(false);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        setDelayedVisible(true);
      }, delay);
    } else {
      setDelayedVisible(false);
    }
  }, [delay, visible]);

  usePinElementPlacement(
    visible,
    anchorRef,
    contentRef,
    pointerRef,
    direction === 'horizontal'
      ? placeCalloutHorizontally
      : placeCalloutVertically,
  );

  return (
    <CSSTransition
      nodeRef={containerRef}
      in={delayedVisible}
      timeout={250}
      unmountOnExit
      appear
    >
      <Layer>
        <div ref={containerRef}>
          <div className={styles.callout} ref={contentRef}>
            <div className={styles.header}>
              <TextSmall>{title}</TextSmall>
              {onDismiss && (
                <div className={styles.dismissButton} onClick={onDismiss}>
                  <Icon icon={IconClearSmall} color="colorWhite" />
                </div>
              )}
            </div>
            {body && <TextTinyLight>{body}</TextTinyLight>}
            {children}
          </div>
          <div className={styles.pointer} ref={pointerRef}>
            <CalloutPointer />
          </div>
        </div>
      </Layer>
    </CSSTransition>
  );
};

const placeCalloutHorizontally: PlacementFunction = (
  anchor,
  content,
  contentEl,
  pointerEl,
) => {
  const horizontalPadding = 12;
  const verticalPadding = 16;

  const top = Math.max(
    verticalPadding,
    Math.min(
      anchor.top + anchor.height / 2 - content.height / 2,
      window.innerHeight - content.height - verticalPadding,
    ),
  );
  const openRight = anchor.right + content.width > window.innerWidth - 32;
  const left = openRight
    ? anchor.left - content.width - horizontalPadding
    : anchor.right + horizontalPadding;
  contentEl.style.top = top + 'px';
  contentEl.style.left = left + 'px';

  if (pointerEl) {
    const pointerTop =
      anchor.top + anchor.height / 2 - pointerEl.clientHeight / 2;

    const pointerLeft = openRight
      ? left + contentEl.clientWidth
      : left - pointerEl.clientWidth;

    pointerEl.style.top = `${pointerTop}px`;
    pointerEl.style.left = `${pointerLeft}px`;
    pointerEl.style.transform = openRight ? 'rotate(180deg)' : '';
  }
};

const placeCalloutVertically: PlacementFunction = (
  anchor,
  content,
  contentEl,
  pointerEl,
) => {
  const verticalPadding = 12;
  const horizontalPadding = 16;

  const left = Math.max(
    horizontalPadding,
    Math.min(
      anchor.left + anchor.width / 2 - content.width / 2,
      window.innerWidth - content.width - horizontalPadding,
    ),
  );
  const openAbove = anchor.top - content.height < 32;
  const top = openAbove
    ? anchor.bottom + verticalPadding
    : anchor.top - content.height - verticalPadding;
  contentEl.style.top = top + 'px';
  contentEl.style.left = left + 'px';

  if (pointerEl) {
    const pointerTop = openAbove
      ? top - pointerEl.clientHeight + 2
      : top + contentEl.clientHeight - 2;

    const pointerLeft =
      anchor.left + anchor.width / 2 - pointerEl.clientWidth / 2;

    pointerEl.style.top = `${pointerTop}px`;
    pointerEl.style.left = `${pointerLeft}px`;
    pointerEl.style.transform = openAbove ? 'rotate(90deg)' : 'rotate(270deg)';
  }
};

export const useCalloutState = ({ key }: { key: string }) => {
  const localStorageKey = `callout-seen-[${key}]`;
  const hasSeen = localStorage.getItem(localStorageKey) === 'true';

  const [visible, setVisible] = React.useState(!hasSeen);

  const dismiss = useStableCallback(() => {
    setVisible(false);
    localStorage.setItem(localStorageKey, 'true');
  });

  return { visible, dismiss };
};

type PlacementFunction = (
  anchor: DOMRect,
  content: { width: number; height: number },
  contentEl: HTMLElement,
  pointerEl: HTMLElement,
) => void;

function usePinElementPlacement(
  visible: boolean,
  anchorRef: React.RefObject<HTMLElement>,
  contentRef: React.RefObject<HTMLElement>,
  pointerRef: React.RefObject<HTMLElement>,
  place: PlacementFunction,
) {
  React.useEffect(() => {
    if (!visible) return;
    let disposed = false;

    function frame() {
      if (disposed) return;
      window.requestAnimationFrame(frame);

      const anchor = anchorRef.current;
      const content = contentRef.current;
      const pointer = pointerRef.current;
      if (!anchor || !content || !pointer) return;

      const anchorRect = anchor.getBoundingClientRect();
      if (anchorRect.width === 0 || anchorRect.height === 0) {
        // Commonly this is the anchor unmounting, so we stay in the same
        // place.  Hopefully the consumer is animating out...
        // But it could also be during mount there's an initial 0x0 rect.
        // So keep ticking :)
        return;
      }
      const contentRect = content.getBoundingClientRect();
      place(anchorRect, contentRect, content, pointer);
    }

    frame();
    return () => {
      disposed = true;
    };
  }, [visible, anchorRef, contentRef, pointerRef, place]);
}
